import { format } from "date-fns"
import { Star } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { dataByPlatform } from "@/constants/platform/data-by-platform"
import type { Platform } from "@/constants/platform/platform"
import { dataByReviewTag } from "@/constants/signal/review-tag"
import type { Signal } from "@/constants/signal/signal"
import type { ReviewWithData } from "@/helpers/company/review-utils"
import { cn } from "@/lib/utils"

function formatDate(dateString: string | null): string {
  if (!dateString) return ""

  try {
    return format(new Date(dateString), "MMM d, yyyy")
  } catch {
    return ""
  }
}

function StarRating({ rating }: { rating: number | null }) {
  if (!rating)
    return <span className="text-muted-foreground text-sm">No rating</span>

  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`size-4 ${
            star <= rating
              ? "fill-brand text-brand"
              : "text-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  )
}

// eslint-disable-next-line complexity -- The view's independent optional fields are clearest together.
export function ReviewCard({
  review,
  signal,
  number,
  shouldHideTags = false,
}: {
  review: ReviewWithData
  signal?: Signal
  number?: number
  shouldHideTags?: boolean
}) {
  const sentiment = signal ? review.signals?.[signal]?.label : null
  const textRef = useRef<HTMLParagraphElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const platformConfig = review.platform
    ? dataByPlatform[review.platform as Platform]
    : null

  useEffect(() => {
    const el = textRef.current
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight)
    }
  }, [review.paraphrasedText])

  if (!review.paraphrasedText) return null

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div>
        <div className="mt-1 flex items-center gap-2">
          <div className="font-bold">
            {number != null && `${number}. `}
            {review.author || "Anonymous"}
          </div>
        </div>
        <div className="wb-label-muted flex items-center gap-1">
          {platformConfig && (
            <span className="text-muted-foreground">
              {platformConfig.title} | {formatDate(review.date)} |
            </span>
          )}
          <StarRating rating={review.rating} />
        </div>
      </div>

      {/* Body */}
      <div className="mt-2 flex-1 text-base">
        <p
          ref={textRef}
          className={cn(
            "font-serif leading-relaxed",
            !isExpanded && "line-clamp-10"
          )}
        >
          {review.paraphrasedText}
        </p>
        {isTruncated && (
          <button
            className="wb-link mt-2 ml-auto block text-sm font-semibold"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "Show less" : "Read more"}
          </button>
        )}

        {/* Sentiment badge and tags */}
        {!shouldHideTags && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {sentiment && (
              <Badge
                size="sm"
                variant={sentiment === "positive" ? "outline" : "bad"}
              >
                {sentiment === "positive" ? "Positive" : "Negative"}
              </Badge>
            )}
            {review.tags.length > 0 &&
              review.tags.map((tag) => (
                <Badge key={tag} size="sm" variant="outline">
                  {dataByReviewTag[tag].title}
                </Badge>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}
